import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { InputNumber } from "primereact/inputnumber";
import { RadioButton } from "primereact/radiobutton";
import { Button } from "primereact/button";
import { useChartsStore } from "../../../store/chartStore";
import { chartIQBrickSize } from "@chartLib/lib/calculator/renkoChartIQ";
import { isFeatureEnabled } from "../../../data/Services/settings";
import ConfigPill, {
  PILL_HINT_STYLE,
  FLYOUT_SECTION_STYLE,
  FLYOUT_LABEL_STYLE,
  RADIO_ROW_STYLE,
  RADIO_ROW_LAST_STYLE,
  RADIO_LABEL_STYLE,
  FLYOUT_INPUT_STYLE,
} from "./ConfigPill";

// Motor novo (port do calculateRenkoBars do HtmlCharts5, #82) — RELEASED na
// 1.4.5 como DEFAULT (paridade provada em tools/htmlchart-oracle/PROVA-RENKO.md).
// O seletor "Motor de cálculo" permite voltar ao motor anterior do WebCharts26
// ("legacy"); a flag renkoEngineSelector vira kill switch do seletor.

/**
 * Calculate auto brick size from chart data
 * Uses the same logic as the renko calculator
 */
function calculateAutoBrickSize(data) {
  if (!data || data.length === 0) return 0.5;

  const sampleSize = Math.min(300, data.length);
  const recentData = data.slice(data.length - sampleSize);

  let minPrice = Infinity;
  let maxPrice = -Infinity;

  recentData.forEach((d) => {
    // Number.isFinite rejects null/undefined/NaN AND +/-Infinity -- a plain
    // !isNaN() check lets Infinity through and poisons priceRange (issue #323).
    const prices = [d.close, d.high, d.low].filter((p) => Number.isFinite(p));
    prices.forEach((p) => {
      if (p < minPrice) minPrice = p;
      if (p > maxPrice) maxPrice = p;
    });
  });

  const priceRange = maxPrice - minPrice;

  if (!isFinite(priceRange) || priceRange <= 0) {
    return 0.5;
  }

  const targetBricks = 20;
  const calculatedSize = priceRange / targetBricks;

  if (!isFinite(calculatedSize) || calculatedSize <= 0) {
    return 0.5;
  }

  return calculatedSize;
}

/**
 * Calculate ATR for brick size
 */
function calculateATR(data, period = 14) {
  if (!data || data.length < period + 1) {
    return null;
  }

  const startIndex = Math.max(1, data.length - period);
  const trueRanges = [];

  for (let i = startIndex; i < data.length; i++) {
    const current = data[i];
    const previous = data[i - 1];

    const highLow = current.high - current.low;
    const highPrevClose = Math.abs(current.high - previous.close);
    const lowPrevClose = Math.abs(current.low - previous.close);

    const trueRange = Math.max(highLow, highPrevClose, lowPrevClose);
    trueRanges.push(trueRange);
  }

  if (trueRanges.length === 0) {
    return null;
  }

  const sum = trueRanges.reduce((acc, tr) => acc + tr, 0);
  return sum / trueRanges.length;
}

/**
 * RenkoBrickSizeIndicator - Shows the Renko brick size on the chart
 *
 * Displays the current brick size (e.g., "5R") and opens a flyout
 * when clicked to allow changing the settings. Chrome shared with
 * PointAndFigureBoxIndicator via ConfigPill.
 */
const RenkoBrickSizeIndicator = ({ chartId, chartData, inline = false }) => {
  const chart = useChartsStore((state) =>
    state.charts.find((c) => c.id === chartId),
  );
  const updateChartConfig = useChartsStore((state) => state.updateChartConfig);

  // Get current renko config
  const currentConfig = chart?.renkoConfig || {
    mode: "auto",
    brickSize: null,
    atrPeriod: 14,
    atrMultiplier: 1.0,
  };

  // Local state for editing
  const [mode, setMode] = useState(currentConfig.mode || "auto");
  const [brickSize, setBrickSize] = useState(currentConfig.brickSize || 50);
  const [atrPeriod, setAtrPeriod] = useState(currentConfig.atrPeriod || 14);
  const [atrMultiplier, setAtrMultiplier] = useState(
    currentConfig.atrMultiplier || 1.0,
  );
  // Motor (#82): "chartiq" = default (paridade HtmlCharts5), "legacy" = anterior
  const [engine, setEngine] = useState(currentConfig.engine || "chartiq");

  // Calculate the effective brick size for display
  const effectiveBrickSize = useMemo(() => {
    const config = chart?.renkoConfig || { mode: "auto" };

    if (config.mode === "fixed" && config.brickSize > 0) {
      return config.brickSize;
    }

    if (config.mode === "atr") {
      const atrValue = calculateATR(chartData, config.atrPeriod || 14);
      if (atrValue && isFinite(atrValue) && atrValue > 0) {
        return atrValue * (config.atrMultiplier || 1.0);
      }
    }

    // auto: cada motor tem sua fórmula (#82)
    if (config.engine === "chartiq") {
      if (!chartData || chartData.length === 0) return 0.5;
      return chartIQBrickSize(chartData);
    }
    return calculateAutoBrickSize(chartData);
  }, [chart?.renkoConfig, chartData]);

  // Format brick size for display - show appropriate decimal places
  const formatBrickSize = (size) => {
    if (size >= 100) {
      // Large values: no decimals
      return Math.round(size).toString();
    } else if (size >= 1) {
      // Values >= 1: up to 2 decimals if needed
      const rounded = Math.round(size * 100) / 100;
      return rounded % 1 === 0
        ? rounded.toString()
        : rounded.toFixed(2).replace(/\.?0+$/, "");
    } else if (size >= 0.01) {
      // Small values: up to 4 decimals
      const rounded = Math.round(size * 10000) / 10000;
      return rounded.toFixed(4).replace(/\.?0+$/, "");
    } else {
      // Very small values: show all 4 decimals
      return size.toFixed(4);
    }
  };

  // Reset local state when panel opens
  const handleOpen = () => {
    const config = chart?.renkoConfig || {
      mode: "auto",
      brickSize: null,
      atrPeriod: 14,
      atrMultiplier: 1.0,
    };
    setMode(config.mode || "auto");
    setBrickSize(config.brickSize || Math.round(effectiveBrickSize));
    setAtrPeriod(config.atrPeriod || 14);
    setAtrMultiplier(config.atrMultiplier || 1.0);
    setEngine(config.engine || "chartiq");
  };

  // Apply changes
  const makeApply = (close) => () => {
    const renkoConfig = {
      mode,
      brickSize: mode === "fixed" ? brickSize : null,
      atrPeriod,
      atrMultiplier,
      engine,
    };

    updateChartConfig(chartId, { renkoConfig });
    close();
  };

  // Get mode label for display
  const getModeLabel = () => {
    const config = chart?.renkoConfig || { mode: "auto" };
    switch (config.mode) {
      case "fixed":
        return "";
      case "atr":
        return "ATR";
      default:
        return "Auto";
    }
  };

  return (
    <ConfigPill
      inline={inline}
      title="Clique para configurar o Renko"
      triggerEvoId="renko-indicator-btn-open-settings"
      flyoutTitle="Configurar Renko"
      onOpen={handleOpen}
      trigger={
        <>
          <span style={PILL_HINT_STYLE}>{getModeLabel()}</span>
          <span>{formatBrickSize(effectiveBrickSize)}R</span>
        </>
      }
    >
      {({ close }) => (
        <>
          {/* Mode Selection */}
          <div style={FLYOUT_SECTION_STYLE}>
            <div style={RADIO_ROW_STYLE}>
              <RadioButton
                inputId={`modeAuto-${chartId}`}
                name={`mode-${chartId}`}
                value="auto"
                checked={mode === "auto"}
                onChange={(e) => setMode(e.value)}
              />
              <label htmlFor={`modeAuto-${chartId}`} style={RADIO_LABEL_STYLE}>
                Automatico
              </label>
            </div>

            <div style={RADIO_ROW_STYLE}>
              <RadioButton
                inputId={`modeFixed-${chartId}`}
                name={`mode-${chartId}`}
                value="fixed"
                checked={mode === "fixed"}
                onChange={(e) => setMode(e.value)}
              />
              <label htmlFor={`modeFixed-${chartId}`} style={RADIO_LABEL_STYLE}>
                Tamanho Fixo
              </label>
            </div>

            <div style={RADIO_ROW_LAST_STYLE}>
              <RadioButton
                inputId={`modeAtr-${chartId}`}
                name={`mode-${chartId}`}
                value="atr"
                checked={mode === "atr"}
                onChange={(e) => setMode(e.value)}
              />
              <label htmlFor={`modeAtr-${chartId}`} style={RADIO_LABEL_STYLE}>
                ATR
              </label>
            </div>
          </div>

          {/* Mode-specific options */}
          {mode === "fixed" && (
            <div style={FLYOUT_SECTION_STYLE}>
              <label style={FLYOUT_LABEL_STYLE}>
                Tamanho do Tijolo (pontos)
              </label>
              <InputNumber
                value={brickSize}
                onValueChange={(e) => setBrickSize(e.value)}
                min={0.0001}
                step={0.0001}
                minFractionDigits={0}
                maxFractionDigits={4}
                style={FLYOUT_INPUT_STYLE}
                inputStyle={FLYOUT_INPUT_STYLE}
                data-evo-id="renko-indicator-input-fixed-size"
              />
            </div>
          )}

          {mode === "atr" && (
            <>
              <div style={{ marginBottom: "0.75rem" }}>
                <label style={FLYOUT_LABEL_STYLE}>Periodo ATR</label>
                <InputNumber
                  value={atrPeriod}
                  onValueChange={(e) => setAtrPeriod(e.value)}
                  min={1}
                  max={100}
                  step={1}
                  showButtons
                  style={FLYOUT_INPUT_STYLE}
                  inputStyle={FLYOUT_INPUT_STYLE}
                  data-evo-id="renko-indicator-input-atr-period"
                />
              </div>
              <div style={FLYOUT_SECTION_STYLE}>
                <label style={FLYOUT_LABEL_STYLE}>Multiplicador</label>
                <InputNumber
                  value={atrMultiplier}
                  onValueChange={(e) => setAtrMultiplier(e.value)}
                  min={0.1}
                  max={10}
                  step={0.1}
                  minFractionDigits={1}
                  maxFractionDigits={2}
                  showButtons
                  style={FLYOUT_INPUT_STYLE}
                  inputStyle={FLYOUT_INPUT_STYLE}
                  data-evo-id="renko-indicator-input-atr-multiplier"
                />
              </div>
            </>
          )}

          {/* Motor (#82): legado vs port fiel do HTML antigo */}
          {isFeatureEnabled("renkoEngineSelector") && (
            <div
              style={{
                marginBottom: "1rem",
                paddingTop: "0.75rem",
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontSize: "12px",
                  color: "#9ca3af",
                }}
              >
                Motor de cálculo
              </label>
              <div style={RADIO_ROW_STYLE}>
                <RadioButton
                  inputId={`engineChartIQ-${chartId}`}
                  name={`engine-${chartId}`}
                  value="chartiq"
                  checked={engine !== "legacy"}
                  onChange={(e) => setEngine(e.value)}
                />
                <label
                  htmlFor={`engineChartIQ-${chartId}`}
                  style={RADIO_LABEL_STYLE}
                >
                  Padrão (HtmlCharts5)
                </label>
              </div>
              <div style={RADIO_ROW_LAST_STYLE}>
                <RadioButton
                  inputId={`engineLegacy-${chartId}`}
                  name={`engine-${chartId}`}
                  value="legacy"
                  checked={engine === "legacy"}
                  onChange={(e) => setEngine(e.value)}
                />
                <label
                  htmlFor={`engineLegacy-${chartId}`}
                  style={RADIO_LABEL_STYLE}
                >
                  Anterior (WebCharts26)
                </label>
              </div>
            </div>
          )}

          {/* Apply Button */}
          <Button
            label="Aplicar"
            onClick={makeApply(close)}
            size="small"
            style={FLYOUT_INPUT_STYLE}
            data-evo-id="renko-indicator-btn-apply"
          />
        </>
      )}
    </ConfigPill>
  );
};

RenkoBrickSizeIndicator.propTypes = {
  chartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  chartData: PropTypes.array,
  periodicity: PropTypes.string,
  inline: PropTypes.bool,
};

export default RenkoBrickSizeIndicator;
